import { useEffect, useState } from 'react';
import { Add, Delete, Lock } from '@mui/icons-material';
import { Box, Card, Divider, Typography, Button, IconButton, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { MemberProfileData } from '../models/member';
import { MemberAdvancedSearchAddProfile } from './memberAdvancedSearchAddProfile';
import { MemberAddItemsWithToast } from './memberAddItemsWithToast';

type MemberAdvancedSearchProfilesType = {
  label: string;
  propName: string;
};

export const MemberAdvancedSearchProfiles = (props: MemberAdvancedSearchProfilesType) => {
  const { label, propName } = props;
  const { t } = useTranslation(['member', 'common']);
  const { setValue, watch } = useFormContext();
  const [localProfileItems, setLocalProfileItems] = useState<MemberProfileData[]>(watch(propName) || []);
  const [showProfileEdit, setShowProfileEdit] = useState<boolean>(false);

  const [toastOpen, setToastOpen] = useState(false);
  const [addedCount, setAddedCount] = useState(0);

  // This function will be called from the parent to trigger the toast
  const handleAddItems = (count: number) => {
    setAddedCount(count);
    setToastOpen(true);
  };

  // Close the toast after it's shown
  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const profileItems = watch(propName);

  const handleDeleteMemberProfile = (memberProfileId: string) => {
    const updatedItems = localProfileItems.filter((item) => item.memberProfileItem_id.toString() !== memberProfileId);
    setLocalProfileItems(updatedItems);
    setValue(propName, updatedItems); // Sync with form state
    handleAddItems(-1);
  };

  const handleProfileAdded = (newProfiles: MemberProfileData[]) => {
    const addProfileItems = newProfiles.filter(
      (newProfile) => !localProfileItems.some((existingProfile) => existingProfile.memberProfileItem_id === newProfile.memberProfileItem_id),
    );

    const updatedItems = [...localProfileItems, ...addProfileItems];
    ConsoleHelper.log('localProfileItems: ', localProfileItems, 'updatedItemsupdatedItems: ', updatedItems);
    setLocalProfileItems(updatedItems);
    setValue(propName, updatedItems); // Sync with form state
    handleAddItems(addProfileItems.length);
    // setShowProfileEdit(false); // Close the profile editor
  };

  useEffect(() => {
    setLocalProfileItems(profileItems || []);
  }, [profileItems]);

  const TableWrapper = ({ children }) => (
    <TableContainer>
      <Table
        size='small'
        sx={{
          '& th': { whiteSpace: 'nowrap', pr: '1rem !important' },
          '& td': { width: '100%' },
          '& td, & th': { border: 0 },
        }}
      >
        <TableBody>{children}</TableBody>
      </Table>
    </TableContainer>
  );

  const renderTableRow = (rowLabel: string, value: string, removeId: string, locked?: boolean) => (
    <TableRow key={`${rowLabel}-${value}-${removeId}`}>
      <TableCell component='th' scope='row' sx={{ verticalAlign: 'top' }}>
        <Typography variant='body1' sx={{ fontWeight: 600 }}>
          {rowLabel}:
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body1'>{value}</Typography>
      </TableCell>
      <TableCell padding='none' sx={{ p: '0 !important' }}>
        {locked ? (
          <Tooltip arrow title={t('member:memberDetails.lockedProfile')} placement='top'>
            <span>
              <IconButton size='small' disabled>
                <Lock fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip arrow title={t('common:button.delete')} placement='top'>
            <span>
              <IconButton size='small' onClick={() => handleDeleteMemberProfile(removeId)}>
                <Delete fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <Card variant='outlined' sx={{ p: 1, m: '-9px 0 0 0' }} component='fieldset'>
        <Typography component='legend' variant='caption' sx={{ px: 0.5, color: (thm) => thm.palette.grey[600] }}>
          {t(label)} <strong>({localProfileItems.length})</strong>
        </Typography>
        <Box sx={{ mt: -1 }}>
          {localProfileItems.length > 0 && (
            <TableWrapper>
              {localProfileItems
                .sort((a: MemberProfileData, b: MemberProfileData) => {
                  if (a.groupName < b.groupName) {
                    return -1;
                  }
                  if (a.groupName > b.groupName) {
                    return 1;
                  }
                  return 0;
                })
                .map((profile) => renderTableRow(profile.groupName, profile.itemName, profile.memberProfileItem_id.toString(), profile.locked))}
            </TableWrapper>
          )}
        </Box>
        {showProfileEdit && (
          <MemberAdvancedSearchAddProfile
            propName={propName}
            callback={(newProfiles) => handleProfileAdded(newProfiles)}
            closeEdit={() => setShowProfileEdit(false)}
          />
        )}
        {!showProfileEdit && localProfileItems.length > 0 && <Divider sx={{ mb: 1 }} />}
        {!showProfileEdit && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', alignContent: 'flex-start' }}>
            <Button variant='outlined' size='small' endIcon={<Add fontSize='small' />} onClick={() => setShowProfileEdit(true)}>
              {t('common:button.add')}
            </Button>
          </Box>
        )}
      </Card>
      <MemberAddItemsWithToast addedCount={addedCount} toastOpen={toastOpen} onClose={handleCloseToast} />
    </>
  );
};
